import { Download, FileCheck } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { PdfTemplateType } from "../../../../pdf-templates/document-template.types";
import { base64ToBlob } from "../../../../lib/utils/utils-functions";

interface PdfPreviewCardProps {
	organizationName: string;
	clientName: string;
	pdfType: PdfTemplateType;
	base64: string;
	settingsDiff: Record<string, any>;
}

const PDF_TYPE_LABELS: Record<PdfTemplateType, string> = {
	[PdfTemplateType.KOSTENVORANSCHLAG]: "Kostenvoranschlag",
	[PdfTemplateType.LIEFERSCHEIN]: "Lieferschein",
	[PdfTemplateType.RECHNUNG]: "Rechnung",
	[PdfTemplateType.SAMMELRECHNUNG]: "Sammelrechnung",
	[PdfTemplateType.RECHNUNGSDOPPEL]: "Rechnungsdoppel",
	[PdfTemplateType.ZAHLUNGSERINNERUNG]: "Zahlungserinnerung",
	[PdfTemplateType.ERSTE_MAHNUNG]: "Erste Mahnung",
	[PdfTemplateType.FOLGEMAHNUNG]: "Folgemahnung",
	[PdfTemplateType.LETZTE_MAHNUNG]: "Letzte Mahnung",
	[PdfTemplateType.BARZAHLUNGSQUITTUNG]: "Barzahlungsquittung",
	[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]: "Kartenzahlungsquittung",
	[PdfTemplateType.AKONTORECHNUNG]: "Akontorechnung",
	[PdfTemplateType.GUTSCHRIFT]: "Gutschrift",
	[PdfTemplateType.AUFTRAGSBLATT]: "Auftragsblatt",
	[PdfTemplateType.ETIK_AUFTRAG]: "Auftragsetikett",
};

// Friendly labels for settings fields
const SETTING_LABELS: Record<string, string> = {
	vat_display_override: "MwSt-Anzeige",
	patient_name_format: "Patientenname-Format",
	is_inv_w_job_title: "Auftragstitel",
	is_inv_w_pat_data: "Patientendaten",
	srg_sorting_option: "Sortierung",
	is_srg_grouped_by_prac: "Nach Behandler gruppiert",
	is_show_insurance_on_pdf: "Versicherung anzeigen",
};

// Format setting values for display
const formatSettingValue = (key: string, value: any): string => {
	if (value === null || value === undefined) return "Nicht gesetzt";
	if (typeof value === "boolean") return value ? "Ja" : "Nein";
	if (key === "vat_display_override") {
		return value === "inclusive"
			? "Inkl."
			: value === "exclusive"
				? "Exkl."
				: String(value);
	}
	if (key === "srg_sorting_option") {
		const optionLabels: Record<string, string> = {
			job_code: "Auftragsnr.",
			job_title: "Auftragstitel",
			patient_last_name: "Patient Nachname",
			patient_code: "Patientennr.",
		};
		return optionLabels[value] || String(value);
	}
	if (key === "patient_name_format") {
		const formatLabels: Record<string, string> = {
			first_last: "Vorname Nachname",
			last_first: "Nachname Vorname",
			last_only: "Nur Nachname",
		};
		return formatLabels[value] || String(value);
	}
	return String(value);
};

export const PdfPreviewCard: React.FC<PdfPreviewCardProps> = ({
	organizationName,
	clientName,
	pdfType,
	base64,
	settingsDiff,
}) => {
	const handleDownload = async () => {
		try {
			const blob = base64ToBlob(base64, "application/pdf");
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${organizationName}_${clientName}_${PDF_TYPE_LABELS[pdfType]}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error downloading PDF:", error);
		}
	};

	const handlePreview = () => {
		try {
			const blob = base64ToBlob(base64, "application/pdf");
			const url = URL.createObjectURL(blob);
			window.open(url, "_blank");
			// Clean up after a delay to ensure the PDF loads
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch (error) {
			console.error("Error previewing PDF:", error);
		}
	};

	// Calculate PDF size
	const sizeKB = Math.round((base64.length * 3) / 4 / 1024);

	// Check if there are any settings differences
	const hasSettings = Object.keys(settingsDiff).length > 0;

	return (
		<div className="border border-gray-200 rounded-lg p-4 bg-white hover:border-blue-300 transition-colors">
			<div className="mb-3">
				<h3 className="font-medium text-gray-900 text-sm mb-1">
					{organizationName}
				</h3>
				<p className="text-xs text-gray-600 mb-1">{clientName}</p>
				<p className="text-xs text-gray-600">
					{PDF_TYPE_LABELS[pdfType]}
				</p>
			</div>

			{hasSettings && (
				<div className="mb-3 flex flex-wrap gap-1">
					{Object.entries(settingsDiff).map(([key, value]) => (
						<div
							key={key}
							className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800"
							title={`${
								SETTING_LABELS[key] || key
							}: ${formatSettingValue(key, value)}`}
						>
							<span className="font-medium">
								{SETTING_LABELS[key] || key}:
							</span>
							<span className="ml-1">
								{formatSettingValue(key, value)}
							</span>
						</div>
					))}
				</div>
			)}

			<div className="relative bg-gradient-to-br from-blue-50 to-gray-50 rounded flex flex-col items-center justify-center py-8 px-4 border border-gray-200">
				<FileCheck className="w-16 h-16 text-blue-600 mb-3" />
				<p className="text-sm font-medium text-gray-900">
					PDF generiert
				</p>
				<p className="text-xs text-gray-500 mt-1">{sizeKB} KB</p>
			</div>

			<div className="flex gap-2 mt-3">
				<Button
					onClick={handlePreview}
					variant="outline"
					size="sm"
					className="flex-1"
				>
					Vorschau
				</Button>
				<Button onClick={handleDownload} size="sm" className="flex-1">
					<Download className="w-4 h-4 mr-1" />
					Download
				</Button>
			</div>
		</div>
	);
};
